import type { OnFlipIdsUpdateArgs, BaseFlipArgs, GetElement, FlippedIds } from "./types";
import type { ScopedSelector } from "./animateFlippedElements/types";
import type { AnimateUnflippedElementsArgs } from "./animateUnflippedElements/types";
import { getFlippedElementPositionsAfterUpdate } from "./getFlippedElementPositions/getFlippedElementPositionsAfterUpdate";
import { toArray } from "../../../../utils/to";
import * as constants from "../constants";
import animateUnflippedElements from "./animateUnflippedElements";
import { animateFlippedElements } from "./animateFlippedElements";

let enabled = true;

export const disableFlip = () => (enabled = false);

export const enableFlip = () => (enabled = true);

export const isFlipEnabled = () => enabled;

const createGetElementFunc = (scopedSelector: ScopedSelector): GetElement => {
	return (id: string) => {
		return scopedSelector(`[${constants.DATA_FLIP_ID}="${id}"]`)[0];
	};
};

const createFlipperScopedSelector = (containerEl: HTMLElement): ScopedSelector => {
	//is to avoid race condition??
	const tempFlipperId = Math.random().toFixed(5);
	containerEl.dataset.flipperId = tempFlipperId;

	return (selector: string) => {
		return toArray(
			containerEl.querySelectorAll(`[data-flipper-id="${tempFlipperId}"] ${selector}`),
		);
	};
};

export const onFlipIdsUpdate = ({
	cachedOrderedFlipIds = [],
	inProgressAnimations = {},
	flippedElementPositionsBeforeUpdate = {},
	flipCallbacks = {},
	containerEl,
	applyTransformOrigin,
	animationNG,
	// spring,
	debug,
	// portalKey,
	// staggerConfig = {},
	decisionData = {},
	// handleEnterUpdateDelete,
	onComplete,
	onStart,
}: OnFlipIdsUpdateArgs) => {
	if (!enabled) return;

	const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
	if (mediaQuery.matches) return;

	const flippedElementPositionsAfterUpdate = getFlippedElementPositionsAfterUpdate({
		element: containerEl,
	});

	const scopedSelector = createFlipperScopedSelector(containerEl);
	const getElement = createGetElementFunc(scopedSelector);

	const isFlipped = (id: string) =>
		flippedElementPositionsBeforeUpdate[id] && flippedElementPositionsAfterUpdate[id];

	const unflippedIds = Object.keys(flippedElementPositionsBeforeUpdate)
		.concat(Object.keys(flippedElementPositionsAfterUpdate))
		.filter((id) => !isFlipped(id));

	const baseArgs: BaseFlipArgs = {
		flipCallbacks,
		getElement,
		flippedElementPositionsBeforeUpdate,
		flippedElementPositionsAfterUpdate,
		inProgressAnimations,
		decisionData,
	};

	const animateUnFlippedElementsArgs = Object.assign({}, baseArgs, {
		unflippedIds,
		animationNG,
	}) as AnimateUnflippedElementsArgs;

	const { hideEnteringElements, animateEnteringElements, animateExitingElements } =
		animateUnflippedElements(animateUnFlippedElementsArgs);

	const flippedIds: FlippedIds = cachedOrderedFlipIds.filter(isFlipped);
	// @ts-ignore
	const animateFlippedElementsArgs: AnimateFlippedElementsArgs = Object.assign({}, baseArgs, {
		containerEl,
		flippedIds,
		applyTransformOrigin,
		// spring,
		debug,
		// staggerConfig,
		scopedSelector,
		onComplete,
	});
	if (onStart) onStart(containerEl, decisionData);

	// the function handles putting flipped elements back in their original positions
	// and returns another function to actually call the flip animation
	const flip = animateFlippedElements(animateFlippedElementsArgs);

	// if (handleEnterUpdateDelete) {
	// 	handleEnterUpdateDelete({
	// 		hideEnteringElements,
	// 		animateEnteringElements,
	// 		animateExitingElements,
	// 		animateFlippedElements: flip,
	// 	});
	// } else {
	hideEnteringElements();
	animateExitingElements().then(animateEnteringElements);
	flip();
	// }
};
